/**
 * Safely parses locationState from React Router navigation.
 *
 * Handles both:
 * - JSON strings (from navigate state serialization)
 * - Plain objects (from direct React Router state)
 * - Malformed input (returns null)
 *
 * This is used by multiple containers during mount to restore filters
 * when navigation passes them in router state.
 */
export function parseLocationState(raw: unknown): Record<string, unknown> | null {
  // Already an object (valid object state from React Router)
  // Exclude arrays, since we only accept plain objects
  if (
    typeof raw === 'object' &&
    raw !== null &&
    !Array.isArray(raw)
  ) {
    return raw as Record<string, unknown>;
  }

  // Try to parse as JSON string
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      // Only return if parsed result is a plain object (not array, not primitive)
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }

  // Invalid input
  return null;
}
